import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Calculator,
  CalendarRange,
  ChefHat,
  CreditCard,
  Globe,
  Handshake,
  Inbox,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MapPin,
  Package,
  ShoppingBag,
  Truck,
  UserCog,
  Users,
  UtensilsCrossed,
  ClipboardList, Menu, Navigation, TrendingUp, Mail, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { canSee } from "@/lib/sections";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/enquiries", label: "Enquiries", icon: Inbox, testid: "nav-enquiries" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/bookings", label: "Bookings", icon: CalendarRange, testid: "nav-bookings" },
  { to: "/prep-sheet", label: "Prep Sheet", icon: ClipboardList, testid: "nav-prep-sheet" },
  { to: "/routes", label: "Routes", icon: Navigation, testid: "nav-routes" },
  { to: "/orders", label: "Orders", icon: ShoppingBag, testid: "nav-orders" },
  { to: "/venues", label: "Venues", icon: MapPin, testid: "nav-venues" },
  { to: "/menus", label: "Menus", icon: ChefHat, testid: "nav-menus" },
  { to: "/staffing", label: "Staffing", icon: UserCog, testid: "nav-staffing" },
  { to: "/equipment", label: "Equipment", icon: Package, testid: "nav-equipment" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/toolkit", label: "F&B Toolkit", icon: Calculator, testid: "nav-toolkit" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const isSuper = user?.platform_role === "super_admin" && !user?.impersonating;
  const [companyName, setCompanyName] = useState("");
  const [pastDue, setPastDue] = useState(false);
  const [trialDays, setTrialDays] = useState(null);
  useEffect(() => {
    if (user?.company_id) {
      api.get("/companies/mine/info").then((r) => setCompanyName(r.data.name || "")).catch(() => {});
      api.get("/billing/status").then((r) => {
        setPastDue(r.data.plan_status === "past_due" && !r.data.fees_waived);
        if (r.data.plan_status === "trialing" && r.data.trial_days_left != null) setTrialDays(r.data.trial_days_left);
      }).catch(() => {});
    }
  }, [user?.company_id]);
  const items = isSuper
    ? [
        { to: "/admin/vendors", label: "Vendors", icon: Building2, testid: "nav-vendors" },
        { to: "/admin/plans", label: "Plans", icon: CreditCard, testid: "nav-plans" },
        { to: "/admin/platform", label: "Platform", icon: Globe, testid: "nav-platform" },
        { to: "/admin/health", label: "Health", icon: TrendingUp, testid: "nav-health" },
        { to: "/admin/emails", label: "Email Log", icon: Mail, testid: "nav-emails" },
      ]
    : [...NAV];
  if (!isSuper && user?.company_id) {
    items.splice(4, 0, { to: "/companies", label: "Companies", icon: Building2, testid: "nav-companies" });
  }
  if (!isSuper && user?.role === "admin" && user?.company_id) {
    items.push({ to: "/hub-settings", label: "Hub Page", icon: Globe, testid: "nav-hub-settings" });
  }

  const exitImpersonation = () => {
    const backup = localStorage.getItem("super_token_backup");
    if (backup) {
      localStorage.setItem("token", backup);
      localStorage.removeItem("super_token_backup");
      window.location.href = "/admin/vendors";
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      {navOpen && (
        <div data-testid="nav-backdrop" onClick={() => setNavOpen(false)} className="fixed inset-0 z-30 bg-black/50 lg:hidden" />
      )}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-ink text-white transition-transform duration-200 lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center bg-brand">
            <UtensilsCrossed className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading text-sm font-bold leading-tight tracking-tight" data-testid="sidebar-company-name">{isSuper ? "CateringDiary" : companyName || "…"}</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">{isSuper ? "Platform Admin" : "Ops Command"}</p>
          </div>
          <button data-testid="mobile-nav-close" onClick={() => setNavOpen(false)} className="ml-auto flex h-8 w-8 items-center justify-center text-white/60 hover:text-white lg:hidden" title="Close menu">
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {items.filter((item) => canSee(user, item.to)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 border-l-2 px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "border-brand bg-white/10 text-white"
                    : "border-transparent text-white/60 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 px-5 py-4">
          <p className="truncate text-xs font-medium text-white/80" data-testid="sidebar-user-email">{user?.email}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{isSuper ? "Super Admin" : user?.role}</p>
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col lg:ml-60">
        {user?.impersonating && (
          <div data-testid="impersonation-banner" className="flex items-center justify-between bg-amber-400 px-6 py-2 text-sm font-semibold text-amber-950">
            <span>Viewing as {user.email} — impersonation mode</span>
            <button data-testid="exit-impersonation" onClick={exitImpersonation} className="font-bold underline">Return to super admin</button>
          </div>
        )}
        {pastDue && !user?.impersonating && (
          <div data-testid="past-due-global-banner" className="flex items-center justify-between bg-red-600 px-6 py-2 text-sm font-semibold text-white">
            <span>Subscription payment failed — update your payment method to keep your paid features.</span>
            <button data-testid="past-due-fix-link" onClick={() => navigate("/hub-settings")} className="font-bold underline">Fix payment</button>
          </div>
        )}
        {trialDays !== null && trialDays <= 4 && !user?.impersonating && !pastDue && (
          <div data-testid="trial-banner" className="flex flex-wrap items-center justify-between gap-2 bg-amber-400 px-6 py-2 text-sm font-semibold text-amber-950">
            <span>{trialDays > 0 ? `Free trial — ${trialDays} day${trialDays === 1 ? "" : "s"} of full access left.` : "Your free trial ends today."}</span>
            <button data-testid="trial-choose-plan" onClick={() => navigate("/hub-settings")} className="font-bold underline">Choose a plan</button>
          </div>
        )}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-white px-4 py-3 sm:px-6">
          <button
            data-testid="mobile-nav-toggle"
            onClick={() => setNavOpen(true)}
            className="mr-3 flex h-9 w-9 items-center justify-center border border-border text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground lg:hidden"
            title="Open menu"
          >
            <Menu className="h-4 w-4" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold leading-tight" data-testid="topbar-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{isSuper ? "Super Admin" : user?.role}</p>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex h-9 w-9 items-center justify-center border border-border text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
